import type { GroupEditSchema } from '@repo/schemas/group/groupEdit';
import type { GroupMemberEditSchema } from '@repo/schemas/group/groupMemberEdit';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useEditGroup } from '@repo/api/group/useEditGroup';
import { useGetGroup } from '@repo/api/group/useGetGroup';
import { useGetGroupUsers } from '@repo/api/group/useGetGroupUsers';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { countStudents } from '@repo/lib/user-lvl/countStudents';
import { groupEditSchema } from '@repo/schemas/group/groupEdit';
import { CheckCircle2Icon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { GroupDialogHeader, GroupUpsertDetailsForm, SelectedUsersSummary, SlotsCounter, UsersSelectList, useGroupUsersFormState } from '@/components/features/manage/manage-groups/GroupUpsertForm';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the EditGroup component.
 * @property {id} [group id] - The group to edit.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface EditGroupDialogProps {
  id?: number;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function EditGroupDialog(editGroupDialogProps: EditGroupDialogProps) {
  const { id, open, onOpenChange } = editGroupDialogProps;
  const {
    tabs,
    setTabs,
    isMainTab,
    searchUserValue,
    setSearchUserValue,
    selectedUsers,
    setSelectedUsers,
    users,
    usersErrorMessage,
    usersIsEmpty,
    usersNoResults,
    getUsersInfinite,
    onRemoveUser,
    onTabsValueChange,
    setSelectedUsersFromIds,
    resetState,
  } = useGroupUsersFormState();
  const { data: groupData, ...groupResults } = useGetGroup(id);
  const { data: groupUsers = [], ...groupUsersResults } = useGetGroupUsers(id);

  const { mutateAsync: groupEdit, isPending } = useEditGroup();

  const form = useForm<GroupEditSchema>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      desc: '',
      slots: null,
    },
    values: groupData && groupEditSchema.decode(groupData),
    resolver: standardSchemaResolver(groupEditSchema.in),
  });

  const slotsValue: number | undefined =
    useWatch({
      control: form.control,
      name: 'slots',
    }) ?? undefined;

  const onSubmit = async (data: GroupEditSchema) => {
    const selectedUserIds = selectedUsers.map((u) => u.id);
    const usersToAdd = selectedUserIds.filter((userId) => !groupUsers.some((groupUser) => groupUser.id === userId));
    const usersToRemove = groupUsers.filter((user) => !selectedUserIds.includes(user.id)).map((user) => user.id);

    const memberChanges: GroupMemberEditSchema[] = [];
    if (usersToAdd.length) {
      memberChanges.push({
        userIds: usersToAdd,
        add: true,
      }) as GroupMemberEditSchema;
    }
    if (usersToRemove.length) {
      memberChanges.push({
        userIds: usersToRemove,
        remove: true,
      });
    }

    const encodedPayload = groupEditSchema.decode(data);

    await groupEdit({
      ...encodedPayload,
      memberChanges: memberChanges,
    })
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const onClose = () => {
    onOpenChange(false);
    resetState();
    form.reset();
  };

  useEffect(() => {
    if (groupUsersResults.isSuccess && open) {
      setSelectedUsers(groupUsers);
    }
  }, [groupUsersResults.isSuccess, open, groupUsers]);

  useEffect(() => {
    if (groupResults.isError) {
      onClose();
      toasterMessageHandler(baseErrorMessages, groupResults.error);
    } else if (groupUsersResults.isError) {
      onClose();
      toasterMessageHandler(baseErrorMessages, groupUsersResults.error);
    }
  }, [groupResults.isError, groupResults.error, groupUsersResults.isError, groupUsersResults.error, onClose]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent isClosable={isMainTab} className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <Form {...form}>
          <DialogBody>
            <LoadingOverlay isLoading={isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa zapisywanie zmian...</LoadingOverlayLabel>
            </LoadingOverlay>
            <LoadingOverlay isLoading={groupResults.isFetching || groupUsersResults.isFetching}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa ładowanie danych...</LoadingOverlayLabel>
            </LoadingOverlay>
            <GroupDialogHeader
              isMainTab={isMainTab}
              title={'Edytuj Grupę'}
              description={'Wprowadź nazwę grupy i dodaj do niej członków.'}
              searchUserValue={searchUserValue}
              onSearchValueChange={setSearchUserValue}
              onBackToMain={() => setTabs('main')}
            />
            <Tabs value={tabs} onValueChange={onTabsValueChange} className={'flex grow flex-col gap-3 overflow-y-auto p-4'}>
              <TabsContent value={'main'}>
                <GroupUpsertDetailsForm formId="edit-group-form" control={form.control} nameField={'name'} descField={'desc'} slotsField={'slots'} onSubmit={form.handleSubmit(onSubmit)} />
                <SelectedUsersSummary selectedUsers={selectedUsers} slotsValue={slotsValue} onRemoveUser={onRemoveUser} onOpenSelector={() => onTabsValueChange('selectedList')} />
              </TabsContent>
              <TabsContent value={'selectedList'}>
                <UsersSelectList
                  selectedUsers={selectedUsers}
                  onSelectionChange={setSelectedUsersFromIds}
                  users={users}
                  usersErrorMessage={usersErrorMessage}
                  usersIsEmpty={usersIsEmpty}
                  usersNoResults={usersNoResults}
                  getUsersInfinite={getUsersInfinite}
                />
              </TabsContent>
            </Tabs>
          </DialogBody>
          {isMainTab ? (
            <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
              <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
                Anuluj
              </Button>
              <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
                <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                  <CheckCircle2Icon />
                  Zapisz
                </LoadingSwap>
              </Button>
            </DialogFooter>
          ) : (
            <DialogFooter className={'border-border-primary dark:bg-card bg-accent items-center justify-between border-t'}>
              <SlotsCounter studentsCount={countStudents(selectedUsers)} slotsValue={slotsValue} />
              <Button onClick={() => setTabs('main')} type={'button'} variant={'tertiary'} className={'ml-atuo'}>
                Zapisz
              </Button>
            </DialogFooter>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  );
}
